import {
  FC,
  memo,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import type { Timeline as ServerTimeline } from "@tsmono/inspect-common/types";
import type { RowSelection } from "@tsmono/inspect-components/selection";
import {
  clearDeepLinkParams,
  dynamicDefaultExcludeEvents,
  kTranscriptCollapseScope,
  kTranscriptOutlineCollapseScope,
  TranscriptLayout,
  type EventNodeContext,
  type SelectOptions,
  type TranscriptCollapseState,
  type TranscriptLayoutRightRailProps,
  type TranscriptViewNodesHandle,
} from "@tsmono/inspect-components/transcript";
import {
  navigateAndForget,
  useChromeNavOwnership,
  useOpenEventFocus,
  useReflectEventNavigationInUrl,
  type ChromeTarget,
} from "@tsmono/react/hooks";
import { isHostedEnvironment } from "@tsmono/util";

import { Events } from "../../../@types/extraInspect";
import { useLogDir } from "../../../app_config";
import { useStore } from "../../../state/store";
import { useCurrentLogFile } from "../../routing/currentSelection";
import {
  makeLogsPath,
  routeFromFullUrl,
  sampleEventUrl,
  toFullUrlMaybe,
  useLogOrSampleRouteParams,
  useLogRouteParams,
  useSampleEventFocusUrlBuilder,
  useSampleUrlBuilder,
} from "../../routing/url";

import { useTranscriptFilter } from "./hooks";

interface TranscriptPanelProps {
  id: string;
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Reset the sample header's scroll-direction anchor (so programmatic scrolls
   *  — j/k, h/l — don't open/collapse the header), folded into the headroom
   *  suppression alongside the swimlane's own anchor. */
  onHeaderResetAnchor?: (debounce?: boolean) => void;
  /** Force the sample header shown/hidden, folded into the transcript's
   *  set-hidden alongside the swimlane headroom (turn-nav landings collapse
   *  both; `k` back past turn 1 re-expands both). */
  onHeaderSetHidden?: (hidden: boolean) => void;
  /** Chrome ownership flag shared with the host's header hook: true while
   *  navigation (deep links, f/h/j/k/l) owns the chrome state — the
   *  natural-scroll detection is fully suppressed until the user physically
   *  scrolls (wheel/touch/pointer), which hands ownership back. */
  chromeNavOwnsRef?: RefObject<boolean>;
  offsetTop?: number;

  // The sample
  running?: boolean;
  backfilling?: boolean;
  /** Whether a live→finished transition may scroll the view to the top —
   *  false for unsuccessful finishes (error/cancelled). */
  scrollToTopOnFinish?: boolean;

  // The transcript data
  events: Events;
  timelines?: ServerTimeline[];

  /** Dynamic default event-filter exclusions, memoized by the host over the
   *  same events (falls back to computing locally). */
  defaultExcludeEvents?: readonly string[];

  /** Extra event-node context (e.g. scan cite labels) merged by the layout. */
  eventNodeContext?: Partial<EventNodeContext>;

  /** Always-visible right rail + optional panel (Search / Scans). */
  rightRail?: TranscriptLayoutRightRailProps;
  /** Evidence selection (the toolbar in SampleDisplay drives it); present
   *  only while selection mode is on. */
  selection?: RowSelection;

  initialEventId?: string | null;
  initialMessageId?: string | null;
  /** Explicit `follow=1` URL param — arm the transcript's live-tail at mount. */
  followRequested?: boolean;
}

/**
 * Renders the Transcript Virtual List, with optional timeline swimlanes
 * when the sample provides timeline data.
 */
export const TranscriptPanel: FC<TranscriptPanelProps> = memo((props) => {
  const {
    id,
    scrollRef,
    onHeaderResetAnchor,
    onHeaderSetHidden,
    chromeNavOwnsRef,
    events,
    running,
    backfilling,
    scrollToTopOnFinish,
    initialEventId,
    initialMessageId,
    followRequested,
    offsetTop,
    timelines: serverTimelines,
    eventNodeContext,
    rightRail,
    defaultExcludeEvents: defaultExcludeEventsProp,
    selection,
  } = props;

  // ---------------------------------------------------------------------------
  // Event type filtering
  // ---------------------------------------------------------------------------

  const storedEventTypes = useStore(
    (state) => state.sample.eventFilter.filteredTypes
  );
  const defaultExcludeEvents = useMemo(
    () => defaultExcludeEventsProp ?? dynamicDefaultExcludeEvents(events),
    [defaultExcludeEventsProp, events]
  );
  const filteredEventTypes = storedEventTypes ?? defaultExcludeEvents;
  const { isDefaultFilter } = useTranscriptFilter(defaultExcludeEvents);

  // ---------------------------------------------------------------------------
  // Store-backed timeline selection adapters
  // ---------------------------------------------------------------------------

  const timelineSelected = useStore((state) => state.sample.timelineSelected);
  const setTimelineSelectedStore = useStore(
    (state) => state.sampleActions.setTimelineSelected
  );
  const activeTimelineIndex = useStore(
    (state) => state.sample.activeTimelineIndex
  );
  const setActiveTimelineIndex = useStore(
    (state) => state.sampleActions.setActiveTimelineIndex
  );

  // Timeline selection is store-backed, but on a user-initiated row click
  // a stale URL `?event=` / `?message=` would otherwise win over the new
  // row's `branchScrollTarget`. Clear those (and reset scroll to top so
  // the post-mount imperative scroll has a clean origin) — but only when
  // this is a user click. Message-resolution-driven selection changes pass
  // `preserveDeepLink: true` because their imperative scroll still needs
  // the deep-link target.
  const [, setSearchParams] = useSearchParams();
  const setTimelineSelected = useCallback(
    (key: string | null, options?: SelectOptions) => {
      setTimelineSelectedStore(key);
      if (options?.preserveDeepLink) return;
      if (!options?.preserveScroll) {
        scrollRef.current?.scrollTo({ top: 0 });
      }
      setSearchParams(
        (prev) => clearDeepLinkParams(new URLSearchParams(prev)),
        { replace: true }
      );
    },
    [setTimelineSelectedStore, setSearchParams, scrollRef]
  );

  const timelineSelection = useMemo(
    () => ({ selected: timelineSelected, onSelect: setTimelineSelected }),
    [timelineSelected, setTimelineSelected]
  );
  const activeTimeline = useMemo(
    () => ({
      activeIndex: activeTimelineIndex,
      onActiveChange: setActiveTimelineIndex,
    }),
    [activeTimelineIndex, setActiveTimelineIndex]
  );

  // ---------------------------------------------------------------------------
  // Collapse state (from store)
  // ---------------------------------------------------------------------------

  const collapsedEvents = useStore((state) => state.sample.collapsedEvents);
  const setCollapsedEventsStore = useStore(
    (state) => state.sampleActions.setCollapsedEvents
  );
  const collapseEventStore = useStore(
    (state) => state.sampleActions.collapseEvent
  );

  const onCollapseTranscript = useCallback(
    (nodeId: string, collapsed: boolean) =>
      collapseEventStore(kTranscriptCollapseScope, nodeId, collapsed),
    [collapseEventStore]
  );
  const onCollapseOutline = useCallback(
    (nodeId: string, collapsed: boolean) =>
      collapseEventStore(kTranscriptOutlineCollapseScope, nodeId, collapsed),
    [collapseEventStore]
  );
  const onSetTranscriptCollapsed = useCallback(
    (ids: Record<string, boolean>) =>
      setCollapsedEventsStore(kTranscriptCollapseScope, ids),
    [setCollapsedEventsStore]
  );
  const onSetOutlineCollapsed = useCallback(
    (ids: Record<string, boolean>) =>
      setCollapsedEventsStore(kTranscriptOutlineCollapseScope, ids),
    [setCollapsedEventsStore]
  );

  const collapseState = useMemo<TranscriptCollapseState>(() => {
    const events = collapsedEvents ?? undefined;
    return {
      transcript: events?.[kTranscriptCollapseScope],
      outline: events?.[kTranscriptOutlineCollapseScope],
      onCollapseTranscript,
      onCollapseOutline,
      onSetTranscriptCollapsed,
      onSetOutlineCollapsed,
    };
  }, [
    collapsedEvents,
    onCollapseTranscript,
    onCollapseOutline,
    onSetTranscriptCollapsed,
    onSetOutlineCollapsed,
  ]);

  // Bulk collapse mode: "collapsed" | "expanded" | null
  // Map to the layout's bulkCollapse?: "collapse" | "expand" prop
  const collapsedMode = useStore((state) => state.sample.collapsedMode);
  const bulkCollapse = useMemo<"collapse" | "expand" | undefined>(() => {
    if (collapsedMode === "collapsed") return "collapse";
    if (collapsedMode === "expanded") return "expand";
    return undefined;
  }, [collapsedMode]);

  // ---------------------------------------------------------------------------
  // Headroom: collapse swimlanes on scroll-down, expand on scroll-up
  // ---------------------------------------------------------------------------

  // Ref to the outline sticky scroll container so its internal scrolling
  // also participates in headroom-direction detection.
  const outlineScrollRef = useRef<HTMLDivElement | null>(null);

  const scrollRefs = useMemo(() => [scrollRef, outlineScrollRef], [scrollRef]);

  // While the find band is open it scrolls matches into view (Ctrl+F → next /
  // prev); those programmatic scrolls would otherwise read as user direction
  // changes and flicker the swimlanes open/closed. Freeze headroom detection
  // while find is active (a ref so the scroll handler sees the live value).
  const showFind = useStore((state) => state.app.showFind);
  const findActiveRef = useRef(showFind);
  findActiveRef.current = showFind;

  // Nav (deep links, f/h/j/k/l, go-to-turn) forces the chrome and suppresses
  // natural scroll detection while it owns it; a physical gesture hands
  // ownership back — see useChromeNavOwnership. The sample header only ever
  // re-expands at the very top (its hook runs stayHiddenOnUpScroll), hence
  // expandOnlyAtTop; the swimlane headroom follows every force.
  const headerTargets = useMemo<ChromeTarget[]>(
    () =>
      onHeaderSetHidden
        ? [{ setHidden: onHeaderSetHidden, expandOnlyAtTop: true }]
        : [],
    [onHeaderSetHidden]
  );
  const {
    hidden: headroomHidden,
    resetAnchor: headroomResetAnchor,
    forceHidden: onHeadroomSetHidden,
  } = useChromeNavOwnership(scrollRefs, {
    ownedForKey: () => !!(initialEventId || initialMessageId),
    findActiveRef,
    navOwnsRef: chromeNavOwnsRef,
    extraTargets: headerTargets,
  });

  const onHeadroomResetAnchor = useCallback(
    (debounce?: boolean) => {
      // Suppress BOTH the swimlane headroom and the sample header headroom, so a
      // programmatic scroll (j/k, h/l, deep-link) doesn't flicker either open.
      headroomResetAnchor(debounce);
      onHeaderResetAnchor?.(debounce);
    },
    [headroomResetAnchor, onHeaderResetAnchor]
  );

  // ---------------------------------------------------------------------------
  // Outline state
  // ---------------------------------------------------------------------------

  const { logPath } = useLogRouteParams();
  const outlineKey = `transcript-panel-${logPath || "na"}`;

  // Use component state for outline collapsed preference
  const outlineCollapsedRaw = useStore((state) => {
    const stored = state.app.propertyBags["collapse-state-scope"]?.[outlineKey];
    return typeof stored === "boolean" ? stored : undefined;
  });
  const setPropertyValue = useStore(
    (state) => state.appActions.setPropertyValue
  );
  const setOutlineCollapsed = useCallback(
    (value: boolean) => {
      setPropertyValue("collapse-state-scope", outlineKey, value);
    },
    [setPropertyValue, outlineKey]
  );
  const outlineCollapsed = outlineCollapsedRaw ?? false;

  const selectedOutlineId = useStore((state) => state.sample.selectedOutlineId);
  const setSelectedOutlineId = useStore(
    (state) => state.sampleActions.setSelectedOutlineId
  );

  // Sync initial event ID to outline selection for deep-link navigation
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (initialEventId) {
      setSelectedOutlineId(initialEventId);
    }
  }, [initialEventId, setSelectedOutlineId]);

  // ---------------------------------------------------------------------------
  // Deep-link URL builder
  // ---------------------------------------------------------------------------

  const builder = useSampleUrlBuilder();
  const {
    logPath: urlLogPath,
    id: urlSampleId,
    epoch: urlEpoch,
  } = useLogOrSampleRouteParams();
  const logFile = useCurrentLogFile();
  const logDir = useLogDir();

  const getEventUrl = useCallback(
    (eventId: string) => {
      let targetLogPath = urlLogPath;
      if (!targetLogPath && logFile) {
        targetLogPath = makeLogsPath(logFile, logDir);
      }
      if (!targetLogPath) return undefined;
      return sampleEventUrl(
        builder,
        eventId,
        targetLogPath,
        urlSampleId,
        urlEpoch
      );
    },
    [builder, urlLogPath, urlSampleId, urlEpoch, logFile, logDir]
  );

  // The shared `getEventUrl` prop is dual-purpose: the copy button copies it
  // verbatim (needs an absolute, shareable URL), while the outline feeds it to
  // `renderLink` below (which strips the origin back off for in-app nav). So
  // the value handed to the layout must be absolute.
  const getFullEventUrl = useCallback(
    (eventId: string) => toFullUrlMaybe(getEventUrl(eventId)),
    [getEventUrl]
  );

  const getEventFocusUrl = useSampleEventFocusUrlBuilder();

  const onNavigatedToEvent = useReflectEventNavigationInUrl(setSearchParams);

  // Outline link clicks are in-view navigation (jumping to an event in the
  // same transcript), so recover the hash route from the absolute URL and
  // use `replace` to keep the back button clean.
  const renderLink = useCallback(
    (url: string, children: ReactNode) => (
      <Link to={routeFromFullUrl(url)} replace>
        {children}
      </Link>
    ),
    []
  );

  // ---------------------------------------------------------------------------
  // Marker navigation (branch markers, error markers, etc.)
  // ---------------------------------------------------------------------------

  const navigate = useNavigate();
  const onOpenEventFocus = useOpenEventFocus();

  const onMarkerNavigate = useCallback(
    (eventId: string, selectedKey?: string) => {
      const url = getEventUrl(eventId);
      if (!url) return;
      if (selectedKey) {
        setTimelineSelected(selectedKey);
      }
      navigateAndForget(navigate, url, { replace: true });
    },
    [getEventUrl, navigate, setTimelineSelected]
  );

  // Outline navigation. Imperative scroll on every click — including
  // re-clicks of the already-selected item — so users can scroll away
  // and click the same outline row to jump back. The URL→scroll effect
  // in TranscriptViewNodes only fires when `event` actually changes,
  // so it can't handle a re-click of the same id by itself.
  const eventsListRef = useRef<TranscriptViewNodesHandle>(null);
  const onOutlineNavigate = useCallback((eventId: string) => {
    eventsListRef.current?.scrollToEvent(eventId);
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <TranscriptLayout
      events={events}
      hiddenEventTypes={filteredEventTypes}
      running={running}
      backfilling={backfilling}
      scrollToTopOnFinish={scrollToTopOnFinish}
      scrollRef={scrollRef}
      offsetTop={offsetTop}
      timeline={{
        selection: timelineSelection,
        active: activeTimeline,
        serverTimelines,
        showSwimlanes: "auto",
        onMarkerNavigate,
      }}
      headroom={{
        hidden: headroomHidden,
        onSetHidden: onHeadroomSetHidden,
        onResetAnchor: onHeadroomResetAnchor,
      }}
      eventNodeContext={eventNodeContext}
      listId={id}
      deepLink={{
        eventId: initialEventId,
        messageId: initialMessageId,
        follow: followRequested,
      }}
      getEventUrl={getFullEventUrl}
      getEventFocusUrl={getEventFocusUrl}
      onOpenEventFocus={onOpenEventFocus}
      onNavigatedToEvent={onNavigatedToEvent}
      keyboardNavDisabled={showFind}
      selection={selection}
      // Only surface the copy-link button where a shared absolute URL is
      // meaningful — not in VS Code webviews or localhost. Matches the message
      // copy-link (SampleDisplay's `enabled: isHostedEnvironment()`).
      linkingEnabled={isHostedEnvironment()}
      bulkCollapse={bulkCollapse}
      collapseState={collapseState}
      eventsListRef={eventsListRef}
      rightRail={rightRail}
      outline={{
        collapsed: outlineCollapsed,
        onCollapsedChange: setOutlineCollapsed,
        toggleTitle: outlineCollapsed
          ? "Show transcript outline"
          : "Hide transcript outline",
        renderLink,
        onNavigateToEvent: onOutlineNavigate,
        selectedId: selectedOutlineId,
        setSelectedId: setSelectedOutlineId,
        scrollRef: outlineScrollRef,
      }}
      empty={{
        text:
          backfilling && isDefaultFilter
            ? "Loading events"
            : running && isDefaultFilter
              ? "Sample is starting"
              : filteredEventTypes.length > 0
                ? "The currently applied filter hides all events."
                : undefined,
        busy: (running || backfilling) && isDefaultFilter,
      }}
    />
  );
});

TranscriptPanel.displayName = "TranscriptPanel";
